import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactModal from 'react-modal';

import Sidebar from './components/Sidebar';
import Header from './components/Header';
 
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import imageCompression from 'browser-image-compression';
/* ---------- GREEN THEME STYLES (scoped-ish) ---------- */
const familyEventsGreenStyles = `
  /* tighten page padding on this page only */
  .content.container-fluid {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .fe-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 12px 0 40px;
  }

  .fe-header {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(10px);
    padding: 14px 22px;
    border-radius: 20px;
    margin-bottom: 10px;
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.06);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .fe-header-content h1 {
    font-size: 1.6rem;
    margin-bottom: 2px;
    font-weight: 800;
    background: linear-gradient(135deg, #75B437 0%, #184A2C 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .fe-header-content p {
    font-size: 0.9rem;
    margin: 0;
    color: #4b5563;
  }

  .fe-header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: nowrap;
  }

  .fe-btn-primary,
  .fe-btn-secondary {
    border-radius: 999px;
    padding: 10px 18px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .fe-btn-primary {
    background: linear-gradient(135deg, #75B437 0%, #184A2C 100%);
    color: #ffffff;
    border: none;
    box-shadow: 0 10px 24px rgba(24, 74, 44, 0.35);
    transition: all 0.25s ease;
  }

  .fe-btn-primary i {
    font-size: 0.9rem;
  }

  .fe-btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(24, 74, 44, 0.45);
  }

  .fe-btn-secondary {
    background: #f3f4f6;
    color: #184A2C;
    border: 1px solid #d1d5db;
    font-weight: 500;
  }

  .fe-btn-secondary i {
    font-size: 0.9rem;
  }

  .fe-btn-secondary:hover {
    background: #e5e7eb;
  }

  .fe-main-grid {
    display: grid;
    grid-template-columns: 1fr 440px;
    gap: 20px;
  }

  .fe-events-panel,
  .fe-calendar-panel {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(10px);
    border-radius: 24px;
    padding: 20px 22px 26px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
    min-height: 420px;
  }

  .fe-panel-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
    padding-bottom: 14px;
    border-bottom: 2px solid #f0f0f0;
  }

  .fe-panel-header-icon {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e9f7ee 0%, #d4efdf 100%);
  }

  .fe-panel-header-icon i {
    color: #1c7c3a;
    font-size: 1rem;
  }

  .fe-panel-header h2 {
    font-size: 1.3rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .fe-panel-header span {
    font-size: 0.85rem;
    color: #6b7280;
  }

  .fe-panel-header-text {
    display: flex;
    flex-direction: column;
  }

  .fe-events-list {
    margin-top: 8px;
    overflow-y: auto;
    max-height: 520px;
    padding-right: 6px;
  }

  .fe-events-list::-webkit-scrollbar {
    width: 6px;
  }

  .fe-events-list::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 999px;
  }

  .fe-events-list::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #75B437 0%, #184A2C 100%);
    border-radius: 999px;
  }

  .fe-event-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 14px 16px 14px 18px;
    margin-bottom: 12px;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .fe-event-card::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #75B437 0%, #184A2C 100%);
  }

  .fe-event-card:hover {
    transform: translateX(3px);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
    border-color: #a7d89c;
  }

  .fe-event-title {
    font-weight: 700;
    font-size: 1rem;
    color: #111827;
    margin-bottom: 6px;
    padding-right: 36px;
  }

  .fe-event-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 6px;
  }

  .fe-event-info-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
    color: #4b5563;
  }

  .fe-event-info-item i {
    font-size: 0.8rem;
    color: #1c7c3a;
    width: 14px;
  }

  .fe-event-description {
    font-size: 0.82rem;
    color: #6b7280;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* ---- improved delete icon ---- */
  .fe-delete-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    border: none;
    background: rgba(248, 250, 252, 0.96);
    color: #dc2626;
    padding: 0;
    width: 26px;
    height: 26px;
    border-radius: 999px;
    font-size: 0.8rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);
    opacity: 0;
    transform: translateY(-2px);
    transition: opacity 0.18s ease, transform 0.18s ease, background 0.18s ease;
  }

  .fe-event-card:hover .fe-delete-btn {
    opacity: 1;
    transform: translateY(0);
  }

  .fe-delete-btn:hover {
    background: rgba(254, 226, 226, 0.95);
  }

  /* always visible on mobile */
  @media (max-width: 768px) {
    .fe-delete-btn {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .fe-empty-state {
    text-align: center;
    padding: 40px 12px;
  }

  .fe-empty-state i {
    font-size: 2.4rem;
    color: #e5e7eb;
    margin-bottom: 8px;
  }

  .fe-empty-state h3 {
    font-size: 1rem;
    color: #111827;
    margin-bottom: 4px;
  }

  .fe-empty-state p {
    font-size: 0.86rem;
    color: #6b7280;
    margin-bottom: 10px;
  }

  .fe-calendar-panel {
    position: relative;
  }

  .fe-calendar-panel .fc {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    font-size: 0.9rem;
  }

  /* toolbar: title + arrows same line */
  .fe-calendar-panel .fc-header-toolbar {
    margin-bottom: 10px;
  }

  .fe-calendar-panel .fc-toolbar-chunk {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .fe-calendar-panel .fc-toolbar-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #184A2C;
  }

  .fe-calendar-panel .fc-button-primary {
    background: linear-gradient(135deg, #75B437 0%, #184A2C 100%);
    border: none;
    border-radius: 8px;
    padding: 4px 12px;
    box-shadow: none;
  }

  .fe-calendar-panel .fc-button-primary:hover {
    filter: brightness(1.05);
  }

  .fe-calendar-panel .fc-button-group {
    margin-left: 4px;
  }

  .fe-calendar-panel .fc-day-today {
    background: #ecf9f0;
  }

  .fe-calendar-panel .fc-h-event,
  .fe-calendar-panel .fc-daygrid-dot-event {
    background: linear-gradient(135deg, #75B437 0%, #184A2C 100%);
    border: none;
    border-radius: 999px;
    padding: 2px 6px;
    font-size: 0.78rem;
    color: #ffffff;
  }

  .fe-calendar-panel .fc-col-header-cell {
    background: #f9fafb;
  }

  .fe-calendar-panel .fc-col-header-cell-cushion {
    padding: 6px 4px;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
  }

  .fe-calendar-panel .fc-daygrid-day-number {
    font-size: 0.8rem;
    color: #111827;
  }

  .fe-event-content {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .fe-event-text {
    flex: 1;
    min-width: 0;
  }

  .fe-event-thumb {
    width: 70px;
    height: 70px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .fe-event-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 576px) {
    .fe-event-thumb {
      width: 56px;
      height: 56px;
    }
  }

  /* ---- CREATE EVENT MODAL STYLING ---- */
  .fe-modal {
    position: relative;
    max-width: 460px;
    width: 100%;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 24px;
    padding: 0;
    outline: none;
    box-shadow: 0 26px 80px rgba(15, 23, 42, 0.45);
  }

  .Overlay {
    background: rgba(15, 23, 42, 0.45) !important;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }

  .fe-modal-header {
    padding: 14px 20px 12px;
    border-radius: 24px 24px 0 0;
    background: linear-gradient(135deg, #75B437 0%, #184A2C 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .fe-modal-header-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .fe-modal-header-text span {
    font-size: 0.75rem;
    opacity: 0.9;
  }

  .fe-modal-header-text h5 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
  }

  .fe-modal-close {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 999px;
    border: none;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #ffffff;
  }

  .fe-modal-body {
    padding: 14px 18px 4px;
    max-height: 70vh;
    overflow-y: auto;
  }

  .fe-modal-footer {
    padding: 10px 18px 16px;
    border-top: 1px solid #f3f4f6;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .fe-modal-section-label {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #9ca3af;
    margin-bottom: 4px;
  }

  .fe-modal-row {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
  }

  .fe-modal-col {
    flex: 1;
  }

  .fe-modal .form-label,
  .fe-modal .control-label {
    font-size: 0.8rem;
    margin-bottom: 2px;
    color: #4b5563;
    font-weight: 600;
  }

  .fe-modal .form-control,
  .fe-modal select,
  .fe-modal textarea {
    border-radius: 12px;
    border-color: #e5e7eb;
    font-size: 0.9rem;
  }

  .fe-modal textarea {
    resize: vertical;
    min-height: 90px;
  }

  .fe-modal-upload-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: nowrap;
  }

  .fe-modal-upload-row small {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .fe-modal-cover-preview {
    width: 100%;
    height: 150px;
    border-radius: 12px;
    overflow: hidden;
    background: #f3f4f6;
    margin-bottom: 8px;
  }

  .fe-modal-cover-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .fe-modal .btn-outline-primary {
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 0.82rem;
  }

  @media (max-width: 576px) {
    .fe-modal-row {
      flex-direction: column;
    }

    .fe-modal-upload-row {
      flex-direction: row;
      align-items: center;
    }

    .fe-modal-footer {
      flex-direction: row;
      justify-content: space-between;
    }
  }

  /* Hide native file input inside modal, use label as trigger */
  .fe-modal #imageUpload {
    display: none;
  }

  @media (max-width: 1200px) {
    .fe-main-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .fe-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .fe-header-actions {
      width: 100%;
      justify-content: space-between;
    }

    .fe-btn-primary,
    .fe-btn-secondary {
      flex: 1;
      justify-content: center;
    }

    .fe-events-panel,
    .fe-calendar-panel {
      min-height: auto;
    }
  }
`;

function FamilyEvents() {
  const { menu, toggleMobileMenu } = useAppLayout();

  const loggedInUserId = localStorage.getItem('user_id');
  const loggedFamilyAdmin = localStorage.getItem('family_admin');

  const [showCalendar, setShowCalendar] = useState(false);

  const queryClient = useQueryClient();

  // -------- Fetch events ----------
  const { data, isLoading, isError } = useQuery({
    queryKey: ['familyEvents'],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-events-list-api`, { withCredentials: true })
        .then((response) => {
          if (response.data.Status === 'Success') {
            return response.data.Data;
          } else {
            throw new Error('Failed to fetch events');
          }
        }),
    staleTime: 0,
    cacheTime: Infinity,
  });

  // IMPORTANT: give FullCalendar an id so it can diff events correctly
  const formattedEvents =
    data?.map((event) => ({
      id: event.id,
      title: event.event_type,
      start: event.event_date,
      extendedProps: event,
    })) || [];

  // -------- Event names ----------
  const [selectedEvent, setSelectedEvent] = useState('');

  const {
    data: eventNames,
    isLoading: isLoadingEventNames,
    isError: isErrorEventNames,
  } = useQuery({
    queryKey: ['eventNames'],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-events-names-api`, { withCredentials: true })
        .then((res) => {
          if (res.data.Status === 'Success') {
            return res.data.Data;
          } else {
            throw new Error('Failed to load event names');
          }
        }),
    staleTime: 0,
    cacheTime: Infinity,
  });

  // -------- Templates ----------
  const {
    data: eventTemplates,
    isLoading: isLoadingEventTemplates,
    isError: isErrorEventTemplates,
  } = useQuery({
    queryKey: ['eventTemplates'],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-events-templates-api`, { withCredentials: true })
        .then((res) => {
          if (res.data.Status === 'Success') {
            return res.data.Data;
          } else {
            throw new Error('Failed to load event templates');
          }
        }),
    staleTime: 0,
    cacheTime: Infinity,
  });

  const [selectedTemplateImage, setSelectedTemplateImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleTemplateSelect = (template) => {
    setSelectedTemplateImage(`${API_URL}/images/${template.image_name}`);
    setUploadedImage(null);
    setEventModalOpenImage(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };

      const compressedFile = await imageCompression(file, options);
      const previewURL = URL.createObjectURL(compressedFile);

      setUploadedImage(previewURL);
      setSelectedFile(compressedFile);
      setSelectedTemplateImage(null);
    } catch (err) {
      console.error('Image compression error:', err);
      toast.error('Failed to compress image.');
    }
  };

  // -------- Modals ----------
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventModalOpenImage, setEventModalOpenImage] = useState(false);
  const [eventModalOpenDetails, setEventModalOpenDetails] = useState(false);
  const [selectedEventDetails, setSelectedEventDetails] = useState(null);

  const [event_title, setTitle] = useState('');
  const [event_date, setEventDate] = useState('');
  const [event_location, setEventLocation] = useState('');
  const [description, setDescription] = useState('');

  
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSaveEvents = async () => {
    if (
      !selectedEvent ||
      !event_title.trim() ||
      !event_date ||
      !event_location ||
      !description.trim()
    ) {
      toast.error('All fields are required');
      return;
    }
  
    if (!selectedFile && !selectedTemplateImage) {
      toast.error('Please upload an image or select a template');
      return;
    }
  
    setIsUpdating(true);
  
    try {
      const formData = new FormData();
  
      formData.append('event_name', selectedEvent);
      formData.append('event_title', event_title);
      formData.append('event_date', event_date);
      formData.append('location', event_location);
      formData.append('description', description);
  
      // 🔹 FINAL compression before upload (~100KB)
      if (selectedFile) {
        const finalOptions = {
          maxSizeMB: 0.1, // ≈ 100KB
          maxWidthOrHeight: 800,
          useWebWorker: true,
        };
  
        const finalCompressedImage = await imageCompression(
          selectedFile,
          finalOptions
        );
  
        formData.append('image_name', finalCompressedImage);
      } 
      // 🔹 Template image (already on server)
      else if (selectedTemplateImage) {
        const imageName = selectedTemplateImage.split('/').pop();
        formData.append('image_name', imageName);
      }
  
      const res = await axios.post(
        `${API_URL}/save-family-events-api`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
  
      if (res.data.Status === 'Success') {
        toast.success('Family event saved successfully');
  
        setSelectedEvent('');
        setTitle('');
        setEventDate('');
        setEventLocation('');
        setDescription('');
        setSelectedFile(null);
        setUploadedImage(null);
        setSelectedTemplateImage(null);
        setEventModalOpen(false);
  
        queryClient.invalidateQueries(['familyEvents']);
      } else {
        toast.error('Failed to save event');
      }
    } catch (err) {
      console.error('Error saving family events:', err);
      toast.error('Error saving family event');
    } finally {
      setIsUpdating(false);
    }
  };
  

  

  const [isDeleting, setIsDeleting] = useState(false);

const handleDeleteEvent = async (eventId) => {
  if (!eventId || isDeleting) return;

  setIsDeleting(true);

  // keep old cache for rollback
  const previousEvents = queryClient.getQueryData(['familyEvents']);

  // optimistic update
  queryClient.setQueryData(['familyEvents'], (old) =>
    Array.isArray(old) ? old.filter((ev) => ev.id !== eventId) : old
  );

  try {
    const res = await axios.delete(
      `${API_URL}/delete-family-event-api/${eventId}`,
      { withCredentials: true }
    );

    if (res.data.Status === 'Success') {
      toast.success('Event deleted');
      queryClient.invalidateQueries(['familyEvents']);
      setEventModalOpenDetails(false);
    } else {
      toast.error('Failed to delete event');
      queryClient.setQueryData(['familyEvents'], previousEvents);
    }
  } catch (err) {
    console.error('Delete error:', err);
    toast.error('Error deleting event');
    queryClient.setQueryData(['familyEvents'], previousEvents);
  } finally {
    setIsDeleting(false);
  }
};

  const formatDateLong = (dateStr) => {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;

    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    const getOrdinalSup = (n) => {
      if (n > 3 && n < 21) return 'th';
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    const ordinal = getOrdinalSup(day);

    return (
      <>
        {day}
        <sup>{ordinal}</sup> {month}, {year}
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="loadingspinnerDiv">
        <div className="loadingspinner"></div>
      </div>
    );
  }

  if (isError) return <div>Failed to load events. Please try again.</div>;

  const upcomingEvents =
    data
      ?.filter((event) => new Date(event.event_date) >= new Date())
      .sort((a, b) => new Date(a.event_date) - new Date(b.event_date)) || [];

  const eventsPanel = (
    <div className="fe-events-panel">
      <div className="fe-panel-header">
        <div className="fe-panel-header-icon">
          <i className="fa fa-calendar" aria-hidden="true"></i>
        </div>
        <div className="fe-panel-header-text">
          <h2>Upcoming Events</h2>
          <span>Tap a card to see full details</span>
        </div>
      </div>
      


      <div className="fe-events-list">

        {upcomingEvents.length === 0 && (
          <div className="fe-empty-state">
            <i className="fa fa-calendar-o" aria-hidden="true"></i>
            <h3>No upcoming events</h3>
            <p>Create your first family event to see it appear here.</p>
            <button
              className="fe-btn-primary"
              onClick={() => setEventModalOpen(true)}
              style={{
                padding: '6px 14px',
                fontSize: '0.8rem',
                borderRadius: '12px',
                boxShadow: '0 4px 14px rgba(24,74,44,0.25)',
              }}
            >
              <i className="fa fa-plus" style={{ fontSize: '0.8rem' }} />
              Create Event
            </button>
          </div>
        )}

        {upcomingEvents.map((event) => (

          <div
            key={event.id}
            className="fe-event-card"
            onClick={() => {
              setSelectedEventDetails(event);
              setEventModalOpenDetails(true);
            }}
          >
            {(parseInt(loggedInUserId) === parseInt(event.user_id) ||
              loggedFamilyAdmin === '1') && (
              <button
                className="fe-delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                 
                    handleDeleteEvent(event.id);
                  
                }}
              >
                <i className="fa fa-trash" aria-hidden="true"></i>
              </button>
            )}

            <div className="fe-event-content">
              <div className="fe-event-text">
                <div className="fe-event-title">{event.event_title}</div>

                <div className="fe-event-info">
                  <div className="fe-event-info-item">
                    <i className="fa fa-calendar" aria-hidden="true"></i>
                    <span>{formatDateLong(event.event_date)}</span>
                  </div>
                  <div className="fe-event-info-item">
                    <i className="fa fa-map-marker" aria-hidden="true"></i>
                    <span>{event.location}</span>
                  </div>
                </div>

                <div className="fe-event-description">{event.description}</div>
              </div>

              {event.image_name && (
                <div className="fe-event-thumb">
                  <img
                    src={`${API_URL}/images/${event.image_name}`}
                    alt={event.event_title}
                    onError={(e) => {
                      e.currentTarget.src = 'img/today-bg.jpg';
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

      </div>
    </div>
  );


  return (
    <div className={`main-wrapper ${menu ? 'slide-nav': ''}`}>
    <Header onMenuClick={(value) => toggleMobileMenu()} />
    <Sidebar />
        <ToastContainer />

        <div className="page-wrapper">
          <div className="content container-fluid">

             <style dangerouslySetInnerHTML={{ __html: familyEventsGreenStyles }} />
            
                        <div className="fe-container">
                          {/* Header */}
                          <div className="fe-header">
                            <div className="fe-header-content">
                              <h1>Family Events</h1>
                              <p>Manage and organize your family gatherings in one place</p>
                            </div>
            
                            <div className="fe-header-actions">
                              <button className="fe-btn-primary" onClick={() => setEventModalOpen(true)}>
                                <i className="fa fa-plus" aria-hidden="true"></i>
                                Create Event
                              </button>
            
                              <button
                                className="fe-btn-secondary"
                                type="button"
                                onClick={() => setShowCalendar((prev) => !prev)}
                              >
                                <i className="fa fa-calendar" aria-hidden="true"></i>
                                {showCalendar ? 'Hide Calendar' : 'View Calendar'}
                              </button>
                            </div>
                          </div>
            
                          {/* Main Content */}
                          {showCalendar ? (
                            <div className="fe-main-grid">
                              {/* Calendar FIRST, then upcoming events */}
                              <div className="fe-calendar-panel">
                                <FullCalendar
                                  plugins={[dayGridPlugin, interactionPlugin]}
                                  initialView="dayGridMonth"
                                  headerToolbar={{
                                    left: '',
                                    center: 'title prev,next',
                                    right: '',
                                  }}
                                  events={formattedEvents}
                                  editable={false}
                                  eventClick={(info) => {
                                    const fullEvent = info.event.extendedProps;
                                    setSelectedEventDetails(fullEvent);
                                    setEventModalOpenDetails(true);
                                    info.jsEvent.preventDefault();
                                  }}
                                />
                              </div>
            
                              {eventsPanel}
                            </div>
                          ) : (
                            eventsPanel
                          )}
                        </div>
            
                        {/* Create / Edit Event Modal */}
                        <ReactModal
                          isOpen={eventModalOpen}
                          onRequestClose={() => setEventModalOpen(false)}
                          className="Modal fe-modal"
                          overlayClassName="Overlay"
                        >
                          <div className="fe-modal-header">
                            <div className="fe-modal-header-text">
                              <span>Family Events</span>
                              <h5>Create an Event</h5>
                            </div>
                            <button
                              type="button"
                              className="fe-modal-close"
                              onClick={() => setEventModalOpen(false)}
                            >
                              <i className="fa fa-times" aria-hidden="true"></i>
                            </button>
                          </div>
            
                          <div className="fe-modal-body">
                            <div className="fe-modal-section-label">Event details</div>
            
                            <div className="fe-modal-row">
                              <div className="fe-modal-col">
                                <label className="control-label">Select Event</label>
                                <select
                                  id="eventSelect"
                                  className="form-control"
                                  name="event_name"
                                  value={selectedEvent}
                                  onChange={(e) => setSelectedEvent(e.target.value)}
                                  required
                                >
                                  <option value="">
                                    {isLoadingEventNames
                                      ? 'Loading events...'
                                      : isErrorEventNames
                                      ? 'Failed to load'
                                      : '-- Select an event --'}
                                  </option>
            
                                  {!isLoadingEventNames &&
                                    !isErrorEventNames &&
                                    eventNames?.map((event) => (
                                      <option key={event.id} value={event.id}>
                                        {event.name}
                                      </option>
                                    ))}
                                </select>
                              </div>
            
                              <div className="fe-modal-col">
                                <label className="form-label">Title</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={event_title}
                                  onChange={(e) => setTitle(e.target.value)}
                                  placeholder="Enter title"
                                />
                              </div>
                            </div>
            
                            <div className="fe-modal-row">
                              <div className="fe-modal-col">
                                <label className="form-label">Event's Date</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={event_date}
                                  onChange={(e) => setEventDate(e.target.value)}
                                  min={new Date().toISOString().split('T')[0]}
                                />
                              </div>
            
                              <div className="fe-modal-col">
                                <label className="form-label">Event's Location</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={event_location}
                                  onChange={(e) => setEventLocation(e.target.value)}
                                  placeholder="Where will it happen?"
                                />
                              </div>
                            </div>
            
                            <div className="fe-modal-section-label" style={{ marginTop: 6 }}>
                              Event cover
                            </div>
            
                            <div className="fe-modal-upload-row">
                              <label
                                htmlFor="imageUpload"
                                className="btn btn-outline-primary d-flex align-items-center gap-2"
                                style={{
                                  color: '#184A2C',
                                  border: '1px solid #75B437',
                                  background: 'none',
                                }}
                              >
                                <i className="fa fa-upload" />
                                Upload
                              </label>
            
                              <small>or</small>
            
                              <button
                                onClick={() => setEventModalOpenImage(true)}
                                className="btn btn-primary btn-sm"
                                style={{
                                  background: 'linear-gradient(135deg, #75B437 0%, #184A2C 100%)',
                                  borderColor: '#75B437',
                                  borderRadius: 9999,
                                  paddingInline: 14,
                                }}
                              >
                                <i className="fa fa-image me-1" />
                                Select Template
                              </button>
            
                              <input
                                type="file"
                                id="imageUpload"
                                className="form-control"
                                accept="image/*"
                                onChange={handleImageUpload}
                              />
                            </div>
            
                            <div className="fe-modal-cover-preview">
                              <img
                                src={uploadedImage || selectedTemplateImage || 'img/today-bg.jpg'}
                                alt="Event cover"
                              />
                            </div>
            
                            <div className="fe-modal-section-label" style={{ marginTop: 6 }}>
                              Description
                            </div>
            
                            <div className="fe-modal-row">
                              <div className="fe-modal-col">
                                <textarea
                                  className="form-control"
                                  rows="4"
                                  value={description}
                                  onChange={(e) => setDescription(e.target.value)}
                                  placeholder="Share details about the event (time, notes, dress code, etc.)"
                                />
                              </div>
                            </div>
                          </div>
            
                          <div className="fe-modal-footer">
                            <button
                              type="button"
                              className="fe-btn-secondary"
                              onClick={() => setEventModalOpen(false)}
                            >
                              Cancel
                            </button>
            
                            
            
            <button
              type="button"
              className="fe-btn-primary"
              onClick={handleSaveEvents}
              disabled={isUpdating}
            >
              {isUpdating ? 'Saving...' : 'Save Event'}
            </button>
            
            
                          </div>
                        </ReactModal>
            
                        {/* Template Picker Modal */}
                        <ReactModal
                          isOpen={eventModalOpenImage}
                          onRequestClose={() => setEventModalOpenImage(false)}
                          className="Modal fe-modal"
                          overlayClassName="Overlay"
                        >
                          <div className="fe-modal-header">
                            <div className="fe-modal-header-text">
                              <span>Event Cover</span>
                              <h5>Select Template</h5>
                            </div>
                            <button
                              type="button"
                              className="fe-modal-close"
                              onClick={() => setEventModalOpenImage(false)}
                            >
                              <i className="fa fa-times" aria-hidden="true"></i>
                            </button>
                          </div>
            
                          <div className="fe-modal-body">
                            <div className="row">
                              {isLoadingEventTemplates && <p>Loading templates...</p>}
                              {isErrorEventTemplates && <p>Failed to load templates.</p>}
            
                              {!isLoadingEventTemplates &&
                                !isErrorEventTemplates &&
                                eventTemplates?.map((template) => (
                                  <div key={template.id} className="col-6 col-md-6 mb-3">
                                    <div className="cardx" style={{ height: '120px', overflow: 'hidden' }}>
                                      <img
                                        src={`${API_URL}/images/${template.image_name}`}
                                        className="card-img-top"
                                        alt={template.event_name}
                                      />
                                    </div>
            
                                    <div className="text-center">
                                      <h6 className="card-title mt-1 mb-0" style={{ fontSize: '0.9rem' }}>
                                        {template.event_name}
                                      </h6>
                                      <button
                                        className="btn btn-sm btn-outline-primary1 mt-1"
                                        onClick={() => handleTemplateSelect(template)}
                                      >
                                        Use Template
                                      </button>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
            
                          <div className="fe-modal-footer">
                            <button
                              type="button"
                              className="fe-btn-secondary"
                              onClick={() => setEventModalOpenImage(false)}
                            >
                              Close
                            </button>
                          </div>
                        </ReactModal>
            
                        {/* Event Details Modal */}
                        <ReactModal
                          isOpen={eventModalOpenDetails}
                          onRequestClose={() => setEventModalOpenDetails(false)}
                          className="Modal fe-modal"
                          overlayClassName="Overlay"
                        >
                          <div className="fe-modal-header">
                            <div className="fe-modal-header-text">
                              <span>Event details</span>
                              <h5>
                                {selectedEventDetails
                                  ? selectedEventDetails.event_type || selectedEventDetails.event_title
                                  : 'Event Details'}
                              </h5>
                            </div>
                            <button
                              type="button"
                              className="fe-modal-close"
                              onClick={() => setEventModalOpenDetails(false)}
                            >
                              <i className="fa fa-times" aria-hidden="true"></i>
                            </button>
                          </div>
            
                          <div className="fe-modal-body">
                            {selectedEventDetails ? (
                              <div className="row">
                                <div className="col-md-6">
                                  <p>
                                    <strong>Title:</strong> {selectedEventDetails.event_title}
                                  </p>
                                  <p>
                                    <strong>Date:</strong> {formatDateLong(selectedEventDetails.event_date)}
                                  </p>
                                </div>
            
                                <div className="col-md-6">
                                  <p>
                                    <strong>Location:</strong> {selectedEventDetails.location}
                                  </p>
                                  <p>
                                    <strong>Description:</strong>
                                  </p>
                                  <p>{selectedEventDetails.description}</p>
                                </div>
            
                                <div className="col-12 mt-3">
                                  <div className="fe-modal-cover-preview" style={{ height: 220 }}>
                                    <img
                                      src={`${API_URL}/images/${selectedEventDetails.image_name}`}
                                      alt="Event Cover"
                                    />
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p>Loading event details...</p>
                            )}
                          </div>
            
                          <div className="fe-modal-footer">
                            <button
                              type="button"
                              className="fe-btn-secondary"
                              onClick={() => setEventModalOpenDetails(false)}
                            >
                              Close
                            </button>
            
                            {selectedEventDetails &&
                              (parseInt(loggedInUserId) === parseInt(selectedEventDetails.user_id) ||
                                loggedFamilyAdmin === '1') && (
                                <button
                                  type="button"
                                  className="fe-btn-primary"
                                  disabled={isDeleting}
            
                                  onClick={() => {
                                    
                                      handleDeleteEvent(selectedEventDetails.id);
                                      setEventModalOpenDetails(false);
                                    
                                  }}
                                >
                                  {isDeleting ? 'Deleting…' : 'Delete Event'}
                                </button>
                              )}
                          </div>
                        </ReactModal>

          </div>
        </div>
      </div>
   
  );
}

export default FamilyEvents;
